import { useRef, type ReactNode } from 'react'
import { useBodyScrollLock, useModalFocusTrap } from '../../hooks/useModalFocusTrap'

export interface ModalOverlayProps {
  open: boolean
  onClose?: () => void
  /** id of the element that labels the dialog (usually the heading). */
  ariaLabelledBy: string
  children: ReactNode
  className?: string
  panelClassName?: string
  closeOnEscape?: boolean
  closeOnBackdrop?: boolean
  align?: 'center' | 'bottom-sheet'
}

/**
 * Accessible modal shell: focus trap, scroll lock, backdrop, dialog semantics.
 * Use for custom panel layouts; prefer ModalDialog when a standard title + body fits.
 */
export function ModalOverlay({
  open,
  onClose,
  ariaLabelledBy,
  children,
  className = '',
  panelClassName = '',
  closeOnEscape = true,
  closeOnBackdrop = true,
  align = 'center',
}: ModalOverlayProps) {
  const dialogRef = useRef<HTMLDivElement>(null)

  useModalFocusTrap({
    active: open,
    containerRef: dialogRef,
    onClose,
    closeOnEscape: closeOnEscape && Boolean(onClose),
  })
  useBodyScrollLock(open)

  if (!open) return null

  const overlayAlign =
    align === 'bottom-sheet'
      ? 'items-end justify-center sm:items-center sm:p-4'
      : 'items-center justify-center p-4 md:py-16'

  return (
    <div className={`fixed inset-0 z-[100] flex ${overlayAlign} ${className}`} role="presentation">
      {closeOnBackdrop && onClose ? (
        <div
          role="presentation"
          className="absolute inset-0 cursor-default bg-background-overlay/60 backdrop-blur-[2px]"
          onClick={onClose}
          onKeyDown={undefined}
        />
      ) : (
        <div role="presentation" aria-hidden="true" className="absolute inset-0 bg-background-overlay/70" />
      )}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={ariaLabelledBy}
        tabIndex={-1}
        className={`relative z-10 outline-none ${panelClassName}`}
      >
        {children}
      </div>
    </div>
  )
}
